import {
  FailedDestinationEntityRecord,
  IDestinationEntityJobRunOptions,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  AccountCredit,
  Brand,
  BuildAccountAggregates,
  Invoice,
  Patient,
  hasMergeConflicts,
  toINamedDocuments,
} from '@principle-theorem/principle-core';
import {
  type IBasePatient,
  type IInvoice,
  type IPatient,
  type IPatientContactDetails,
  type IStaffer,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  FirestoreMigrate,
  Timestamp,
  all$,
  doc,
  getError,
  omitByKeys,
  resolveSequentially,
  runTransaction,
  snapshotCombineLatest,
  toNamedDocument,
  type DocumentReference,
  type IIdentifiable,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, sumBy } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { PATIENT_DEPOSIT_RESOURCE_TYPE } from '../../../destination/entities/patient-deposits';
import {
  BasePatientInvoiceDestinationEntity,
  IInvoiceBuildData,
  IInvoiceUpdateData,
  IPatientInvoiceJobData,
  IPatientInvoiceMigrationData,
  PATIENT_INVOICE_DESTINATION_ENTITY,
  PATIENT_INVOICE_RESOURCE_TYPE,
  PATIENT_TRANSACTION_RESOURCE_TYPE,
} from '../../../destination/entities/patient-invoices';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import { PatientBalanceSourceEntity } from '../../source/entities/patient-balance';
import { PatientDiscountSourceEntity } from '../../source/entities/patient-discounts';
import { PatientPaymentAdjustmentSourceEntity } from '../../source/entities/patient-payment-adjustments';
import { PatientPaymentSourceEntity } from '../../source/entities/patient-payments';
import { PatientWriteOffSourceEntity } from '../../source/entities/patient-treatment-write-offs';
import { PatientTreatmentsSourceEntity } from '../../source/entities/patient-treatments';
import {
  OasisPatient,
  PatientSourceEntity,
} from '../../source/entities/patients';
import { OasisItemCodeMappingHandler } from '../mappings/item-codes';
import { OasisStafferMappingHandler } from '../mappings/staff';
import { OasisInvoiceBuilder } from './lib/oasis-invoice-builder';
import { PatientDestinationEntity } from './patients';

export const patientInvoiceDestinationEntity =
  DestinationEntity.withMetadataDescription(
    PATIENT_INVOICE_DESTINATION_ENTITY,
    `
      Oasis has a flat table of payments, transactions, adjustments, discounts, and write offs (incoming and outgoing). To deal with this, any charges made on the same day are grouped into an invoice and their line items will be built by any found treatments.

      Afterwards, all payments are ordered by date and we apply payments to the oldest invoices first. Any overpayment that exists at the end will be converted to account credit.
    `
  );

export class PatientInvoicesDestinationEntity extends BasePatientInvoiceDestinationEntity<
  OasisPatient,
  IPatientInvoiceJobData<OasisPatient>
> {
  override destinationEntity = patientInvoiceDestinationEntity;
  patientSourceEntity = new PatientSourceEntity();

  override canMigrateByIdRange = true;

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    discounts: new PatientDiscountSourceEntity(),
    paymentAdjustments: new PatientPaymentAdjustmentSourceEntity(),
    payments: new PatientPaymentSourceEntity(),
    writeOffs: new PatientWriteOffSourceEntity(),
    treatments: new PatientTreatmentsSourceEntity(),
    balances: new PatientBalanceSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new OasisStafferMappingHandler(),
    itemCodes: new OasisItemCodeMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInvoiceJobData<OasisPatient>[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const brand$ = PracticeMigration.brand$(migration).pipe(
      map(toNamedDocument)
    );
    const practitioners$ = brand$.pipe(
      switchMap((brand) => all$(Brand.stafferCol(brand))),
      toINamedDocuments()
    );
    const sourceItemCodes$ =
      this.customMappings.itemCodes.getRecords(translationMap);

    return snapshotCombineLatest([
      staff$,
      brand$,
      practitioners$,
      sourceItemCodes$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([patients, [staff, brand, practitioners, sourceItemCodes]]) =>
        patients.map((sourcePatient) => ({
          sourcePatient,
          staff,
          brand,
          practitioners,
          sourceItemCodes,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientInvoiceJobData<OasisPatient>
  ): Promise<
    | IPatientInvoiceMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const practice = await Firestore.getDoc(
      migration.configuration.practices[0].ref
    );
    const patientId = data.sourcePatient.data.id;
    const patientRef = await translationMap.getDestination<IPatient>(
      patientId.toString(),
      PATIENT_RESOURCE_TYPE
    );
    if (!patientRef) {
      return this.buildErrorResponse(
        data.sourcePatient.record,
        `Couldn't resolve patient`
      );
    }

    const patient = await Firestore.getDoc(patientRef);

    const sourceTransactions = await this.sourceEntities.payments.filterRecords(
      migration,
      'accountPatientId',
      patientId
    );

    const sourceTreatments = await this.sourceEntities.treatments.filterRecords(
      migration,
      'accountPatientId',
      patientId
    );

    const sourceDiscounts = await this.sourceEntities.discounts.filterRecords(
      migration,
      'accountPatientId',
      patientId
    );

    const sourcePaymentAdjustments =
      await this.sourceEntities.paymentAdjustments.filterRecords(
        migration,
        'accountPatientId',
        patientId
      );

    const sourceWriteOffs = await this.sourceEntities.writeOffs.filterRecords(
      migration,
      'accountPatientId',
      patientId
    );

    const balance = sumBy(
      sourceTransactions,
      (transaction) =>
        transaction.data.claimAmount ?? transaction.data.amount ?? 0
    );

    const invoiceBuilder = new OasisInvoiceBuilder(
      sourceTreatments,
      sourceTransactions,
      sourceDiscounts,
      sourcePaymentAdjustments,
      sourceWriteOffs,
      balance,
      data.staff,
      data.practitioners,
      data.sourceItemCodes,
      translationMap,
      migration
    );

    try {
      invoiceBuilder.buildAccountSummary(practice.ref);
      invoiceBuilder.validateAccountSummary();

      const invoiceData = await invoiceBuilder.buildInvoices(
        patient as IBasePatient & IPatientContactDetails,
        practice
      );

      return {
        patientRef: patient.ref,
        data: invoiceData,
        createdAt: Timestamp.now(),
      };
    } catch (error) {
      return this.buildErrorResponse(
        data.sourcePatient.record,
        getError(error)
      );
    }
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: IPatientInvoiceJobData<OasisPatient>,
    migrationData: IPatientInvoiceMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const invoiceRefs = await this._upsertInvoices(
        migrationData,
        translationMap,
        migrationData.patientRef
      );

      const accountAggregateBuilder = new BuildAccountAggregates(console);
      await accountAggregateBuilder.updatePatient(
        migrationData.patientRef,
        false
      );

      return this._buildSuccessResponse(jobData.sourcePatient, invoiceRefs);
    } catch (error) {
      return this.buildErrorResponse(
        jobData.sourcePatient.record,
        getError(error)
      );
    }
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    migrationData: IPatientInvoiceMigrationData
  ): Promise<IPatientInvoiceMigrationData | undefined> {
    const invoiceMergeConflicts: (IInvoiceBuildData | undefined)[] =
      await resolveSequentially(
        migrationData.data,
        async (invoiceBuildData) => {
          try {
            const invoiceRef = await translationMap.getDestination<IInvoice>(
              invoiceBuildData.invoice.uid,
              PATIENT_TRANSACTION_RESOURCE_TYPE
            );

            if (!invoiceRef) {
              return;
            }

            const existingInvoice = await Firestore.getDoc(invoiceRef);
            const invoiceMergeConflict = hasMergeConflicts(
              omitByKeys(invoiceBuildData.invoice, ['from', 'to', 'reference']),
              omitByKeys(existingInvoice, ['from', 'to', 'reference']),
              [],
              [
                {
                  key: 'transactions',
                  sortByPath: 'reference',
                },
              ]
            );

            const existingTransactions: (IIdentifiable & ITransaction)[] = [];

            const transactionMergeConflicts = await resolveSequentially(
              invoiceBuildData.transactions,
              async (transaction) => {
                try {
                  const existingTransaction = await Firestore.getDoc(
                    doc(
                      Invoice.transactionCol(existingInvoice),
                      transaction.uid
                    )
                  );

                  existingTransactions.push({
                    ...existingTransaction,
                  });

                  return hasMergeConflicts(transaction, existingTransaction);
                } catch {
                  return false;
                }
              }
            );

            const hasMergeConflict = [
              invoiceMergeConflict,
              ...transactionMergeConflicts,
            ].some((mergeConflict) => mergeConflict);

            if (hasMergeConflict) {
              return {
                ...invoiceBuildData,
                invoice: {
                  ...existingInvoice,
                  uid: invoiceBuildData.invoice.uid,
                },
                transactions: existingTransactions,
              };
            }
          } catch {
            return;
          }
        }
      );

    if (invoiceMergeConflicts.some((conflict) => conflict)) {
      return {
        ...migrationData,
        data: compact(invoiceMergeConflicts),
      };
    }
  }

  private async _upsertInvoices(
    migrationData: IPatientInvoiceMigrationData,
    translationMap: TranslationMapHandler,
    patientRef: DocumentReference<IPatient>
  ): Promise<IInvoiceUpdateData[]> {
    const invoiceData = await runTransaction(async (firestoreTransaction) =>
      resolveSequentially(migrationData.data, async (invoiceBuildData) => {
        const invoiceDestinationRef =
          await translationMap.getDestination<IInvoice>(
            invoiceBuildData.invoice.uid,
            PATIENT_INVOICE_RESOURCE_TYPE
          );

        const invoiceRef = await FirestoreMigrate.upsertDoc(
          Patient.invoiceCol({
            ref: patientRef,
          }),
          { ...invoiceBuildData.invoice, deleted: false },
          invoiceDestinationRef?.id,
          firestoreTransaction
        );

        if (!invoiceDestinationRef) {
          await translationMap.upsert({
            sourceIdentifier: invoiceBuildData.invoice.uid,
            destinationIdentifier: invoiceRef,
            resourceType: PATIENT_INVOICE_RESOURCE_TYPE,
          });
        }

        const transactionRefs = await resolveSequentially(
          invoiceBuildData.transactions,
          async (transaction) => {
            const transactionDestinationRef =
              await translationMap.getDestination<ITransaction>(
                invoiceBuildData.invoice.uid,
                PATIENT_TRANSACTION_RESOURCE_TYPE
              );

            const transactionRef = await FirestoreMigrate.upsertDoc(
              Invoice.transactionCol({
                ref: invoiceRef,
              }),
              { ...transaction, deleted: false },
              transaction.uid,
              firestoreTransaction
            );

            if (!transactionDestinationRef) {
              await translationMap.upsert({
                sourceIdentifier: transaction.uid,
                destinationIdentifier: transactionRef,
                resourceType: PATIENT_TRANSACTION_RESOURCE_TYPE,
              });
            }

            return transactionRef;
          }
        );

        const resolvedInvoice = {
          ...invoiceBuildData.invoice,
          ref: invoiceRef,
        };

        const accountCredits = Invoice.getDueCredits(resolvedInvoice, []);

        const accountCreditRefs = await resolveSequentially(
          accountCredits,
          async (accountCredit) => {
            const accountCreditDestinationRef =
              await translationMap.getDestination<ITransaction>(
                invoiceBuildData.invoice.uid,
                PATIENT_DEPOSIT_RESOURCE_TYPE
              );

            const accountCreditRef = await FirestoreMigrate.upsertDoc(
              AccountCredit.col({
                ref: patientRef,
              }),
              { ...accountCredit, deleted: false },
              accountCredit.depositUid,
              firestoreTransaction
            );

            if (!accountCreditDestinationRef) {
              await translationMap.upsert({
                sourceIdentifier: accountCredit.depositUid,
                destinationIdentifier: accountCreditRef,
                resourceType: PATIENT_DEPOSIT_RESOURCE_TYPE,
              });
            }

            return accountCreditRef;
          }
        );

        return {
          invoiceRef,
          transactionRefs,
          accountCreditRefs,
        };
      })
    );

    return compact(invoiceData);
  }
}
